import React, { useState, useMemo } from 'react';
import { Search, Eye, Cpu, Heart } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { Card, CardContent } from '../../../../components/ui/card';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { cn } from '../../../../lib/utils';
import GrowPageContext from '../contexts/GrowPageContext';
import useGrow from '../hooks/useGrow';
import DefaultTasksDialog from './components/DefaultTasksDialog';
import SkillTreeView from './components/SkillTreeView';
import SkillsPreview from './components/SkillsPreview';

const TABS = [
  { id: 0, label: 'Hard Skills', icon: Cpu },
  { id: 1, label: 'Soft Skills', icon: Heart },
  { id: 2, label: 'Preview', icon: Eye },
];

function GrowPageV3() {
  const { department } = useParams();
  const [saved, setSaved] = useState(false);
  const [isDialogOpened, setIsDialogOpened] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    isOpen: false,
    message: '',
  });

  const {
    isLoading,
    activeTab,
    handleChange,
    growLevels,
    selectedLvls,
    setSelectedLvls,
    foundedSkills,
    handleSearch,
    setSearchValue,
    softSkillTree,
    hardSkillTree,
    setHardSkillTree,
    setSoftSkillTree,
    filteredHardTree,
    filteredSoftTree,
    initialTreeDataHard,
    initialTreeDataSoft,
    setInitialTreeDataHard,
    setInitialTreeDataSoft,
  } = useGrow();

  const debouncedSearch = useDebouncedCallback(handleSearch, 500);

  const growPageProviderValue = useMemo(
    () => ({ department, growLevels, setIsDialogOpened }),
    [department, growLevels, setIsDialogOpened],
  );

  const handleTabChange = (tabId) => {
    handleChange(null, tabId);
  };

  return (
    <GrowPageContext.Provider value={growPageProviderValue}>
      <ScrollArea className="h-full">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Cpu className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Grow Skills</h1>
                <p className="text-muted-foreground text-sm">
                  Manage skill trees for {department} department
                </p>
              </div>
            </div>

            {/* Search - only show for non-preview tabs */}
            {activeTab !== 2 && (
              <div className="relative p-0.5">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search skills..."
                  onChange={(e) => {
                    setSearchValue(e.target.value.trim());
                    debouncedSearch();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className={cn(
                    'w-64 pl-9 pr-4 py-2 rounded-lg text-sm',
                    'bg-background border border-input',
                    'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
                    'placeholder:text-muted-foreground',
                  )}
                />
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Main Content */}
          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : (
                <>
                  {activeTab === 0 && (
                    <SkillTreeView
                      type="hard"
                      treeData={hardSkillTree}
                      setTreeData={setHardSkillTree}
                      setInitialTreeData={setInitialTreeDataHard}
                      initialTreeData={initialTreeDataHard}
                      saved={saved}
                      setSaved={setSaved}
                      errorMessage={errorMessage}
                      setErrorMessage={setErrorMessage}
                      foundedSkills={foundedSkills}
                      growLevels={growLevels}
                    />
                  )}
                  {activeTab === 1 && (
                    <SkillTreeView
                      type="soft"
                      treeData={softSkillTree}
                      setTreeData={setSoftSkillTree}
                      setInitialTreeData={setInitialTreeDataSoft}
                      initialTreeData={initialTreeDataSoft}
                      saved={saved}
                      setSaved={setSaved}
                      errorMessage={errorMessage}
                      setErrorMessage={setErrorMessage}
                      foundedSkills={foundedSkills}
                      growLevels={growLevels}
                    />
                  )}
                  {activeTab === 2 && (
                    <SkillsPreview
                      hardSkillsData={filteredHardTree}
                      softSkillsData={filteredSoftTree}
                      growLevels={growLevels}
                      selectedLvls={selectedLvls}
                      setSelectedLvls={setSelectedLvls}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Success Toast */}
        {saved && (
          <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg">
            Saved successfully!
            <button type="button" onClick={() => setSaved(false)} className="ml-2 font-bold">
              ×
            </button>
          </div>
        )}

        {/* Error Toast */}
        {errorMessage.isOpen && (
          <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg">
            {errorMessage.message}
            <button
              type="button"
              onClick={() => setErrorMessage({ isOpen: false, message: '' })}
              className="ml-2 font-bold"
            >
              ×
            </button>
          </div>
        )}
      </ScrollArea>

      {isDialogOpened && (
        <DefaultTasksDialog
          isOpened={isDialogOpened}
          commonHardSkillTree={initialTreeDataHard}
          commonSoftSkillTree={initialTreeDataSoft}
          setCommonHardSkillTree={setInitialTreeDataHard}
          setCommonSoftSkillTree={setInitialTreeDataSoft}
          saved={saved}
          setSaved={setSaved}
          activeTab={activeTab}
        />
      )}
    </GrowPageContext.Provider>
  );
}

export default GrowPageV3;
